import { FormControlLabel } from '@mui/material';
import { Checkbox } from '@mui/material';
import { Grid } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { AccordionFamilyCoexistence } from 'shell/view-models/accordion-family-coexistence';
import { memo, useMemo } from 'react';
import ChooseDate from 'shell/components/shared/choice-date';
import ListFiscalCodeID from 'shell/components/shared/fiscal-code-id';
import ForeignStateList from 'shell/components/shared/foreign-state-list';
import ListGender from 'shell/components/shared/gender';
import MunicipalityForeignList from 'shell/components/shared/municipality-foreign-list';
import MunicipalityList from 'shell/components/shared/municipality-list';
import { PmiTextField } from 'shell/components/shared/text-field';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { noop } from 'shell/utils/noop';
import { AccordionPersonForm as AccordionPersonFormVM } from 'shell/view-models/accordion-person-form';
import { CitizenshipVM } from 'shell/view-models/citizenship';

interface AccordionPersonFormProps {
  disabled?: boolean;
  values: AccordionPersonFormVM | null;
  family?: AccordionFamilyCoexistence;
  citizenship?: Array<CitizenshipVM>;
  errors?: FormikErrors<AccordionPersonFormVM>;
  touched?: FormikTouched<AccordionPersonFormVM>;
  onChange: (
    key: keyof AccordionPersonFormVM,
    value: AccordionPersonFormVM[keyof AccordionPersonFormVM]
  ) => void;
  isForPersonDetailPage?: boolean;
  statoCFGeneralita?: string;
}

const AccordionPersonFormComponent = ({
  disabled,
  values,
  touched,
  errors,
  onChange,
  family,
  citizenship,
  isForPersonDetailPage = false,
}: AccordionPersonFormProps): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const isParentHoodVisibile: boolean = useMemo(() => {
    if (!isForPersonDetailPage) return true;
    return userProfile?.datiLogin?.profile?.feature?.visualizzaRapportoParentela === '1';
  }, [
    userProfile?.datiLogin?.profile?.feature?.visualizzaRapportoParentela,
    isForPersonDetailPage,
  ]);

  const isCitizenshipVisible: boolean = useMemo(() => {
    if (!isForPersonDetailPage) return true;
    return userProfile?.datiLogin?.profile?.feature?.displayCittadinanze === '1';
  }, [userProfile?.datiLogin?.profile?.feature?.displayCittadinanze, isForPersonDetailPage]);
  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Codice fiscale"
          aria-label="Codice fiscale"
          id="fiscalCode"
          fullWidth
          onChange={(event) => {
            onChange('fiscalCode', event?.target.value);
          }}
          value={values?.fiscalCode?.toUpperCase() || ''}
          inputProps={{ maxLength: '16' }}
          disabled={disabled}
          error={Boolean(errors?.fiscalCode) && touched?.fiscalCode}
          helperText={errors?.fiscalCode}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <ListFiscalCodeID
          isConflicting={values?.validityFiscalCodeId !== '1'}
          disabled
          onChange={noop}
          value={values?.validityFiscalCodeId || ''}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Stato"
          aria-label="Stato"
          id="state"
          fullWidth
          onChange={(event) => {
            onChange('state', event?.target.value);
          }}
          value={values?.state || ''}
          disabled={disabled}
          error={Boolean(errors?.state) && touched?.state}
          helperText={errors?.state}
        />
      </Grid>
      <Grid
        item
        container
        spacing={0}
        rowSpacing={{ xs: 1, md: 0 }}
        columnSpacing={1}
        xs={12}
        md={10}
        alignItems="center"
      >
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Cognome"
            aria-label="Cognome"
            id="surname"
            fullWidth
            onChange={(event) => {
              onChange('surname', event?.target.value);
            }}
            disabled={disabled}
            value={values?.surname || ''}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <ListGender required value={values?.gender || ''} onChange={noop} disabled />
        </Grid>
      </Grid>
      <Grid
        item
        container
        spacing={0}
        rowSpacing={{ xs: 1, md: 0 }}
        columnSpacing={1}
        xs={12}
        md={10}
        alignItems="center"
      >
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Nome"
            aria-label="Nome"
            id="name"
            fullWidth
            disabled={disabled}
            onChange={(event) => {
              onChange('name', event?.target.value);
            }}
            value={values?.name || ''}
          />
        </Grid>
        {isParentHoodVisibile ? (
          <Grid item xs={12} md={3}>
            <PmiTextField
              label="Parentela"
              aria-label="Parentela"
              id="relationship"
              onChange={noop}
              fullWidth
              value={family?.descrizioneCodiceLegame || ''}
              disabled
            />
          </Grid>
        ) : undefined}
      </Grid>
      <Grid item xs={12} md={4}>
        <ChooseDate
          label="Data di nascita"
          selected={values?.birthDate || null}
          disabled
          onChange={noop}
          choice={values?.birthChoiceDate}
        />
      </Grid>
      {!values?.isForeign ? (
        <>
          <Grid item xs={12} md={3}>
            <MunicipalityList
              value={values?.italian?.municipality || null}
              onChange={noop}
              disabled
              label="Comune nascita"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              aria-label="Provincia nascita"
              label="Provincia nascita"
              id="province"
              name="province"
              fullWidth
              disabled
              type="text"
              variant="outlined"
              value={values?.italian?.municipality?.province || ''}
              onChange={noop}
            />
          </Grid>
          {values?.italian?.municipalityIsValid ? undefined : (
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="municipalityIsNotValid"
                    checked={true}
                    onChange={noop}
                    inputProps={{ 'aria-label': 'Comune non valido alla data' }}
                    disabled
                  />
                }
                label="Comune non valido alla data"
              />
            </Grid>
          )}
        </>
      ) : (
        <>
          <Grid item xs={12} md={3}>
            <ForeignStateList
              value={values?.foreign?.state || null}
              onChange={noop}
              disabled
              label="Stato estero di nascita"
              required
              id="foreignStateBirth"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MunicipalityForeignList
              value={values?.foreign?.municipality || null}
              onChange={noop}
              label="Comune estero di nascita"
              required
              id="foreignMunicipality"
              disabled
              foreignStateIstatCode={values?.foreign?.state?.id}
            />
          </Grid>
        </>
      )}
      {isCitizenshipVisible ? (
        <Grid item xs={12} md={2}>
          <PmiTextField
            label="Cittadinanza"
            aria-label="Cittadinanza"
            id="citizenship"
            fullWidth
            disabled
            onChange={noop}
            value={(citizenship || [])[0]?.citizenship || ''}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

AccordionPersonFormComponent.displayName = 'Accordion person form component';

const AccordionPersonForm = memo(AccordionPersonFormComponent);

export default AccordionPersonForm;
